import { useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApp } from '../../contexts/AppContext';
import { Button } from '../../components/ui';
import {
  PlaygroundEmbed,
  GrowthChart,
  EmotionalCard,
} from '../../components/shared';
import { track, EVENTS } from '../../utils/analytics';
import { buildSignupUrl } from '../../utils/signupUrl';

export default function Results() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { state, resetApi, resetQuizAnswers, resetExpressInput } = useApp();
  const { api, flow } = state;
  const isExpress = flow === 'express';
  const emailSentRef = useRef(false);

  useEffect(() => {
    if (api.status !== 'success' || !api.result || !api.result.id) {
      navigate('/quiz', { replace: true });
    }
  }, [api.status, api.result, navigate]);

  useEffect(() => {
    track(EVENTS.RESULTS_PAGE_VIEWED, { flow });
  }, []);

  useEffect(() => {
    if (emailSentRef.current) return;
    const email = state.auth?.email;
    const pgId = id || api.result?.id;
    if (!email || !pgId) return;

    emailSentRef.current = true;

    const signupUrl = buildSignupUrl({
      quizAnswers: state.quizAnswers,
      expressInput: state.expressInput,
      flow: state.flow,
      email,
    });

    const apiBase = import.meta.env.VITE_API_URL || '';
    fetch(`${apiBase}/api/send-playground-email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, playground_id: pgId, map: api.result?.map, signup_url: signupUrl }),
    }).catch((err) => console.error('Failed to send playground email:', err));
  }, []);

  const mapData = api.result?.map;

  if (!api.result || !api.result.id) {
    return null;
  }

  const playgroundId = id || api.result.id!;

  function handleStartOver() {
    track(EVENTS.CREATE_ANOTHER_PLAN_CLICKED, { flow });
    resetApi();
    resetQuizAnswers();
    resetExpressInput();
    navigate('/quiz');
  }

  return (
    <div className="results-page">
      <div className="results-page__container">
        {/* Header */}
        <div className="results-header">
          <h1 className="results-header__plan-name">
            {isExpress ? 'Your Personalized Financial Setup' : 'Your Personalized Financial Blueprint'}
          </h1>
          <p className="results-header__personalized">
            {isExpress
              ? 'Built from your description, tailored to how your money actually works'
              : 'Crafted from thousands of proven strategies, tailored to your unique goals and income'}
          </p>
        </div>

        {/* Map */}
        <PlaygroundEmbed playgroundId={playgroundId} />
        <p className="results-disclaimer">
          *Balances shown are estimates based on your inputs. Connect Sequence to see your real numbers.
        </p>

        {/* Growth Chart */}
        {mapData && <GrowthChart mapData={mapData} />}

        {/* Emotional Card */}
        <EmotionalCard />

        {/* CTAs */}
        <div className="results-cta">
          <Button variant="secondary" size="lg" onClick={handleStartOver} className="results-cta__secondary">
            {isExpress ? 'Create Another Setup' : 'Create Another Blueprint'}
          </Button>

          <Button
            variant="primary"
            size="lg"
            onClick={() => {
              track(EVENTS.SEE_BREAKDOWN_CLICKED, { flow });
              navigate('/plan');
            }}
          >
            {isExpress ? 'See Your Step-by-Step Setup' : 'See Your Step-by-Step Plan'}
          </Button>
        </div>
      </div>
    </div>
  );
}
